import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAnomalies, getDistribution, getFunnel, getHealth,
  getOverview, getSummary, getTimeseries, getTop
} from '../api/kpi.client';
import type {
  AnomaliesParams, DistributionParams, FunnelParams,
  OverviewParams, SummaryParams, TimeseriesParams, TopParams
} from '../types';

const env = (typeof globalThis !== 'undefined' && (globalThis as any).__APP_ENV__) || {};
const tokenKey = (env.tokenKey as string) ?? 'authToken';
const hasToken = () => (typeof window === 'undefined' ? true : Boolean(window.localStorage.getItem(tokenKey)));

export const useKpiHealth = () => useQuery({ queryKey: ['kpi', 'health'], queryFn: getHealth, staleTime: 60_000, enabled: hasToken() });
export const useKpiOverview = (params: OverviewParams | undefined) => useQuery({
  queryKey: ['kpi', 'overview', params],
  queryFn: () => getOverview(params),
  enabled: hasToken(),
  staleTime: 30_000,
});
export const useKpiSummary = (params: SummaryParams | undefined) => useQuery({ queryKey: ['kpi', 'summary', params], queryFn: () => getSummary(params as SummaryParams), enabled: Boolean(params?.collection) && hasToken(), staleTime: 30_000 });
export const useKpiTimeseries = (params: TimeseriesParams | undefined) => useQuery({ queryKey: ['kpi', 'timeseries', params], queryFn: () => getTimeseries(params as TimeseriesParams), enabled: Boolean(params?.collection) && hasToken() });
export const useKpiTop = (params: TopParams | undefined) => useQuery({ queryKey: ['kpi', 'top', params], queryFn: () => getTop(params as TopParams), enabled: Boolean(params?.collection) && hasToken() });
export const useKpiDistribution = (params: DistributionParams | undefined) => useQuery({ queryKey: ['kpi', 'distribution', params], queryFn: () => getDistribution(params as DistributionParams), enabled: Boolean(params?.collection) && hasToken() });
export const useKpiFunnel = (params: FunnelParams | undefined) => useQuery({ queryKey: ['kpi', 'funnel', params], queryFn: () => getFunnel(params as FunnelParams), enabled: Boolean(params?.collection) && hasToken() });
export const useKpiAnomalies = (params: AnomaliesParams | undefined) => useQuery({ queryKey: ['kpi', 'anomalies', params], queryFn: () => getAnomalies(params as AnomaliesParams), enabled: Boolean(params?.collection) && hasToken() });

export const useQueryParams = <T extends object>(defaults: T, search: URLSearchParams): T => useMemo(() => {
  const merged: any = { ...defaults };
  search.forEach((value, key) => {
    const base = defaults[key as keyof T];
    merged[key] = Array.isArray(base) ? value.split(',').filter(Boolean) : typeof base === 'number' ? Number(value) : value;
  });
  return merged as T;
}, [defaults, search]);
